import { Playbook } from '@/types/playbook/playbook';
import { Bot, CheckCircle, Clock, DollarSign, ShoppingCart, User } from 'lucide-react';

export const limitedTimeOffer: Playbook = {
  meta: {
    id: 'limited-time-offer',
    slug: 'limited-time-offer',
    categoryId: 'amplify-marketing',
    name: 'The Limited-Time Offer',
    tagline: 'Create urgency with countdown timers.',
    description: 'Present time-sensitive deals with countdown timers to create urgency and drive immediate purchasing action.',
    strategy: 'Present time-sensitive deals with countdown timers to create urgency and drive immediate action.',
    benefits: [
      'Create purchasing urgency',
      'Increase conversion rates',
      'Drive immediate action',
      'Boost revenue during campaigns'
    ],
    targetAudience: ['E-commerce stores', 'SaaS companies', 'Online services'],
    estimatedSetupTime: '15 minutes',
    difficulty: 'beginner',
    tags: ['urgency', 'limited-time', 'discounts', 'conversions'],
    isFeatured: true
  },
  seo: {
    metaTitle: 'Limited-Time Offer Playbook - Create Urgency with a Genie',
    metaDescription: 'Drive immediate sales with Genie-powered limited-time offers. Create urgency and boost conversions with countdown timers.',
    keywords: ['limited time offer', 'urgency marketing', 'countdown timer', 'flash sale', 'conversion optimization']
  },
  demoConfig: {
    triggerType: 'delayed',
    delay: 3000,
    message: "⏰ Special offer! Get 30% off for the next 24 hours. I can apply this discount to your cart right now. Interested?",
    position: 'center'
  },
  demoScript: [
    { speaker: 'agent', message: "⏰ Flash sale! 30% off ends in 2 hours. Should I apply it to your cart?" },
    { speaker: 'user', message: "How much would I save?" },
    { speaker: 'agent', message: "You'd save $149 on the annual plan. That's like getting 4 months free!" },
    { speaker: 'user', message: "That's a great deal" },
    { speaker: 'agent', message: "It really is! Should I lock in this price for you now?" }
  ],
  templateConfig: {
    firstMessage: "⏰ Limited time offer! [DISCOUNT] off for the next [TIME]. Want to take advantage?",
    systemPrompt: "You are a sales Genie promoting limited-time offers. Create urgency while being helpful and honest. Focus on value and benefits.",
    tools: ['discount_codes', 'cart_management', 'countdown_timer']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'Visitor shows purchase interest',
      description: 'User browses products, visits pricing page, or shows buying signals'
    },
    {
      type: 'genie',
      icon: Clock,
      genieIcon: 'genie-lamp',
      label: 'Presents time-sensitive offer',
      description: 'Genie triggers limited-time offer with clear countdown timer and urgency messaging'
    },
    {
      type: 'action',
      icon: DollarSign,
      genieIcon: 'settings',
      label: 'Explains value proposition',
      description: 'Quantifies savings amount and highlights the special nature of the discount'
    },
    {
      type: 'action',
      icon: Bot,
      genieIcon: 'settings',
      label: 'Handles objections',
      description: 'Addresses concerns and reinforces value while maintaining urgency'
    },
    {
      type: 'action',
      icon: ShoppingCart,
      genieIcon: 'settings',
      label: 'Facilitates immediate purchase',
      description: 'Applies discount code and guides user through checkout process'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'smile',
      label: 'Urgency-driven conversion',
      description: 'Customer completes purchase motivated by limited-time opportunity'
    }
  ],
  metrics: {
    conversionRate: '24%',
    responseTime: '<1 second',
    satisfactionScore: '4.3/5',
    costSavings: '$80k/campaign',
    timeToValue: 'Immediate'
  },
  testimonials: [
    {
      companyName: 'TechDeals Store',
      authorName: 'Amanda Rodriguez',
      authorTitle: 'E-commerce Manager',
      quote: 'Limited-time offer campaigns increased our conversion rate by 32%. The urgency messaging really works when delivered conversationally.',
      metrics: '32% conversion rate increase'
    }
  ],
  resources: [
    {
      type: 'template',
      title: 'Urgency Marketing Scripts',
      description: 'Proven conversation templates for different types of limited-time offers',
      url: '/templates/urgency-marketing'
    },
    {
      type: 'guide',
      title: 'Countdown Timer Integration',
      description: 'Technical setup for real-time countdown displays and expiration handling',
      url: '/docs/countdown-integration'
    }
  ]
};